import { ComposerContextChipRow } from "src/components/llm/components/composer-context-chip";
import { AdStudioTemplatePickerModal } from "src/pages/ad-studio/components/ad-studio-template-picker-modal";
import { useState } from "react";
import { Box, Column, FolderIcon } from "@hightouch/htui";

import {
  type ContextItem,
  ContextRow,
} from "src/components/llm/components/compact-chat-input";
import {
  FillEmailPreview,
  type SelectedEmailAsset,
} from "src/components/llm/component-picker";
import { buildAdStudioTemplateContextItems } from "src/pages/ad-studio/components/ad-studio-template-context-items";
import type { ComposerSelections } from "src/pages/projects/amp-composer/composer-selections";
import type {
  ComposerOptions,
  SelectedOptions,
} from "src/pages/projects/amp-composer/use-composer-options";

type ComposerAttachmentsProps = {
  selected: SelectedOptions;
  options: Pick<ComposerOptions, "adTemplates" | "adTemplateDrafts">;
  /** The raw selections, so a removal keeps the picks hidden by the brand scope. */
  selections: ComposerSelections;
  onChange: (change: Partial<ComposerSelections>) => void;
};

/** The ad templates and email assets attached to the next message, as removable pills above the text. */
export function ComposerAttachments({
  selected,
  options,
  selections,
  onChange,
}: ComposerAttachmentsProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const folderChips = selected.adTemplateFolders.map((folder) => ({
    id: folder.id,
    label: `Templates: ${folder.name} · ${folder.templates.length} available`,
    icon: FolderIcon,
    removeLabel: `Remove template folder ${folder.name}`,
    onClick: () => setIsPickerOpen(true),
    onRemove: () =>
      onChange({
        adTemplateFolderIds: selections.adTemplateFolderIds.filter(
          (id) => id !== folder.id,
        ),
      }),
  }));
  return (
    <Column gap={2}>
      <ComposerContextChipRow
        chips={folderChips}
        layout="row"
        onRemove={(chip) => chip.onRemove()}
      />
      <ContextRow
        contextItems={[
          ...buildAdStudioTemplateContextItems(
            selected.adTemplates,
            selected.adTemplates.map(({ id }) => id),
          ),
          ...[...selected.emailTemplates, ...selected.emailComponents].map(
            emailAssetItem,
          ),
        ]}
        onRemoveContextItem={(itemId) => {
          const without = (assets: SelectedEmailAsset[]) =>
            assets.filter(({ id }) => id !== itemId);
          onChange({
            adTemplateIds: selections.adTemplateIds.filter(
              (id) => id !== itemId,
            ),
            adTemplateFolderIds: selections.adTemplateFolderIds.filter(
              (id) => id !== itemId,
            ),
            emailTemplates: without(selections.emailTemplates),
            emailComponents: without(selections.emailComponents),
          });
        }}
      />
      {isPickerOpen && (
        <AdStudioTemplatePickerModal
          templates={options.adTemplates}
          drafts={options.adTemplateDrafts}
          initialSelection={{
            ids: selections.adTemplateIds,
            folderIds: selections.adTemplateFolderIds,
            folderBrandProfileId: selected.adTemplateFolderBrandProfileId,
          }}
          onClose={() => setIsPickerOpen(false)}
          onConfirm={({ ids, folderIds, folderBrandProfileId }) => {
            onChange({
              adTemplateIds: ids,
              adTemplateFolderIds: folderIds ?? [],
              adTemplateFolderBrandProfileId: folderBrandProfileId,
            });
            setIsPickerOpen(false);
          }}
        />
      )}
    </Column>
  );
}

function emailAssetItem(asset: SelectedEmailAsset): ContextItem {
  const preview = (size: string) => (
    <Box width={size} height={size} overflow="hidden" bg="white">
      <FillEmailPreview
        html={asset.html}
        wrapAsComponent={asset.kind === "component"}
      />
    </Box>
  );
  return {
    id: asset.id,
    label: asset.name,
    thumbnail: preview("32px"),
    popoverPreview: preview("200px"),
    referenceAssetId: null,
  };
}
